<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import DataTable from '../UI/Table/DataTable.vue'
import TableMenu from '../UI/Table/TableMenu.vue'
import PageLayout from '../UI/PageLayout.vue'
import MainContainer from '../UI/Content/MainContainer.vue'
import BlockContent from '../UI/Content/BlockContent.vue'
import CardContent from '../UI/Content/CardContent.vue'
import { useNotificationApi } from '../../../../Composables/Notification/useNotificationApi.ts'
import { useQueryParameter } from '../../../../Composables/useQueryParameter.ts'

const { t } = useI18n()
const { fetchPaginatedNotifications } = useNotificationApi()

const notificationListTable = ref<any>(null)
const queryParam = useQueryParameter('s')

const selectedStatus = ref<number>(
  queryParam.value !== null && queryParam.value !== undefined && !isNaN(Number(queryParam.value))
    ? Number(queryParam.value)
    : 0,
)
const currentSearchQuery = ref<string>('')

const getHashQueryParam = (paramName: string): string | null => {
  const hash = window.location.hash
  const queryString = hash.includes('?') ? hash.split('?')[1] : ''
  const urlParams = new URLSearchParams(queryString)
  return urlParams.get(paramName)
}

const isTeamView = computed(() => getHashQueryParam('team') === 'true')

const submenuLabels = computed(() => [
  { id: 0, name: t('notification.tabs.all') },
  { id: 1, name: t('notification.tabs.unread') },
  { id: 2, name: t('notification.type.actionRequired') },
  { id: 3, name: t('notification.tabs.completed') },
])

const notificationColumns = computed(() => [
  { name: 'id', displayable: false },
  {
    name: 'createdAt',
    title: t('notification.list.table.createdAt'),
    formatter: 'custom',
    options: {
      renderer: (data: string) => {
        if (!data) return '-'
        const date = new Date(data)
        const formattedDate = date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })
        const formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }).replace(':', 'h')
        return `${formattedDate} ${t('common.at')} ${formattedTime}`
      },
    },
  },
  {
    name: 'title',
    title: t('notification.list.table.title'),
    searchable: true,
    formatter: 'context',
    options: {
      renderType: 'contracts',
      renderId: 'contextId',
    },
  },
  {
    name: 'isRead',
    title: t('notification.list.table.isRead'),
    formatter: 'custom',
    options: {
      renderer: (value: boolean, type: string, row: any) => {
        if (value && row.readAt) {
          const date = new Date(row.readAt)
          const formattedDate = date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })
          return `${t('notification.list.table.readOn')} ${formattedDate}`
        }
        return value ? t('main.bool.true') : t('main.bool.false')
      },
    },
  },
  {
    name: 'requiresAction',
    title: t('notification.type.actionRequired'),
    formatter: 'custom',
    options: {
      renderer: (value: boolean) => {
        return value ? t('main.bool.true') : t('main.bool.false')
      },
    },
  },
  ...(isTeamView.value
    ? [
        {
          name: 'recipientName',
          title: t('notification.list.table.recipient'),
        },
      ]
    : []),
])

const notificationOrder = [['createdAt', 'desc']]

const setSelectedStatus = (value: string | number) => {
  const parsedValue = Number(value)
  if (parsedValue !== selectedStatus.value) {
    selectedStatus.value = parsedValue
    notificationListTable.value?.renderDataTable()
  }
}

const getNotifications = async (
  callback: Function,
  {
    page,
    limit,
    search,
    sort = [],
  }: { page: number; limit: number; search?: string; sort?: Array<{ field: string; order: string }> },
) => {
  currentSearchQuery.value = search || ''

  const alertTypeParam = getHashQueryParam('alertType')
  const alertType =
    alertTypeParam !== null && alertTypeParam !== undefined && alertTypeParam !== ''
      ? Number(alertTypeParam)
      : undefined

  const teamParam = getHashQueryParam('team')
  const isTeam = teamParam === 'true'

  let statusParams = {}
  if (selectedStatus.value === 1) {
    statusParams = { isRead: 0 }
  } else if (selectedStatus.value === 2) {
    statusParams = { type: 'ALERT_ACTION_REQUIRED' }
  } else if (selectedStatus.value === 3) {
    statusParams = { status: 'COMPLETED' }
  }

  let sortParams = {}
  for (const { field, order } of sort) {
    sortParams = { ...sortParams, [`sort[${field}]`]: order }
  }

  const params: Record<string, any> = {
    page,
    limit,
    ...(alertType !== undefined && !isNaN(alertType) && { alertType }),
    ...(isTeam && { team: true }),
    ...statusParams,
    ...(search ? { q: search } : {}),
    ...sortParams,
  }

  const response = await fetchPaginatedNotifications(params)

  callback({
    data: response.items,
    recordsTotal: response.total,
    recordsFiltered: response.total,
  })
}

const handleHashChange = () => {
  notificationListTable.value?.renderDataTable()
}

onMounted(() => {
  window.addEventListener('hashchange', handleHashChange)
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange)
})

const dynamicZeroRecords = computed(() => {
  if (currentSearchQuery.value.trim() !== '') {
    return t('table.zeroRecords')
  }
  return t('notification.list.emptyInitial')
})
</script>

<template>
  <PageLayout menu="notifications-index">
    <MainContainer :title-t="t('notification.header.title')">
      <BlockContent>
        <TableMenu
          endpoint="notifications"
          :status="selectedStatus"
          :filters="[]"
          :show-filters="false"
          :show-filters-button="false"
          :show-calendar-filter="false"
          :show-download-button="false"
          :submenu-labels="submenuLabels"
          @set-selected-status="setSelectedStatus"
        />
        <CardContent>
          <DataTable
            ref="notificationListTable"
            id="notificationListTable"
            pagination-mode="server"
            first-load
            :columns="notificationColumns"
            :order="notificationOrder"
            :page-length="25"
            :fetch="getNotifications"
            :zero-records="dynamicZeroRecords"
          />
        </CardContent>
      </BlockContent>
    </MainContainer>
  </PageLayout>
</template>
